// src/DuchetDashboardPage.tsx
// Ситуационный центр «Диспансерный учёт» — по ТЗ TZ-vizual-duchet-dashboard.md (2026-07-18).
// Оболочка (фон/шапка/фильтры) — тема существующих дашбордов (HospitalDashboardsPage: #083264/#0e2b4b).
// Новое по ТЗ — только контент: KPI-панель (6) + 10 виджетов + светофор + разворот + drill-down.
// Данные: /api/v1/duchet/dashboard/{bootstrap,summary,widgets}; drill -> /dashboards/duchet/cards.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import "@tabler/icons-webfont/dist/tabler-icons.min.css";
import { PeriodCalendar } from "./PeriodCalendar";

// ── ТЕМА ОБОЛОЧКИ (из существующих дашбордов — НЕ перекрашивать): шапка/фильтры/фон страницы ──
const SHELL = {
  page: "#083264",          // фон страницы (как HospitalDashboardsPage)
  card: "#0e2b4b",          // фон панели фильтров
  border: "rgba(255,255,255,0.2)",
  text: "#dce6f2",
  textMuted: "rgba(255,255,255,0.6)",
  btnBg: "#083264",
};

// ── ВНУТРЕННОСТЬ «разворота» — строго по эталонному макету (situacionnyy_centr_duchet_rich_dashboard) ──
const W = {
  wrap: "#0a1220",          // фон большой панели-разворота
  card: "#101d30",          // фон виджета
  cardBorder: "#223b57",
  track: "#0a1526",         // незаполненная часть баров/колец
  divider: "#1a2c44",
  rowBorder: "#16283f",     // разделители строк таблицы движения
  chipBg: "#13253d",
  chipBorder: "#2b4d72",
};

// ── ПАЛИТРА ДАННЫХ (ТЗ §1.1) ──
const D = {
  green: "#25c274", greenText: "#5dcaa5",
  yellow: "#f5b423",
  red: "#f0554f", redText: "#f0908c",
  blue: "#4b9fe8", blue2: "#3f8fd9", blue3: "#2f7fca", blue4: "#185fa5",
  violet: "#8479e6", violetText: "#a79ef0",
  orange: "#e8703a",
  gray: "#9aa0aa", grayText: "#b4b2a9", grayDim: "#647a92",
  label: "#7d93ad", widgetTitle: "#a7c0dc", axis: "#6f86a1", axisVal: "#9fb3cc",
  rowText: "#c3d2e4", hint: "#54708f",
};

// ── Фоны цветных карточек, тёмная тема (ТЗ §1.2) ──
const TONES = {
  blue:   { bg: "#0e2540", border: "#2f6fb0", label: "#8fb4dd", sub: "#5a83ad", accent: D.blue },
  green:  { bg: "#0d2e26", border: "#1d9e75", label: "#6fd3b0", sub: "#4c8f79", accent: D.green },
  yellow: { bg: "#33280c", border: "#d19822", label: "#e2b968", sub: "#a8842f", accent: D.yellow },
  red:    { bg: "#361718", border: "#d0403c", label: "#f0908c", sub: "#b0605d", accent: D.red },
  violet: { bg: "#221d40", border: "#6a5fd8", label: "#a79ef0", sub: "#7d74c4", accent: D.violet },
  gray:   { bg: "#1a2536", border: "#4a596e", label: "#9fb0c6", sub: "#6f829a", accent: "#8fa2ba" },
} as const;
type ToneKey = keyof typeof TONES;

// ── ПОРОГИ СВЕТОФОРА — КОНФИГ (ТЗ §1.4, не хардкодить в логике) ──
const SEMAPHORE = {
  areaCoverage: { greenFromPctOfAvg: 110, redBelowPctOfAvg: 80 }, // охват участка vs среднее по орг.
  plan: { greenFromPct: 90, yellowFromPct: 70 },                  // выполнение плана осмотров
};
function areaColor(coverage: number, avg: number): string {
  if (avg <= 0) return D.blue;
  const rel = (coverage / avg) * 100;
  if (rel >= SEMAPHORE.areaCoverage.greenFromPctOfAvg) return D.green;
  if (rel < SEMAPHORE.areaCoverage.redBelowPctOfAvg) return D.red;
  return D.yellow;
}

// ── api ──
function apiBase(): string {
  const env = (import.meta as any).env;
  return String(env?.VITE_DASHBOARDS_API_BASE || "/dashboards-api").replace(/\/+$/, "");
}
function publicBase(): string {
  const env = (import.meta as any).env;
  return String(env?.VITE_DASHBOARDS_PUBLIC_BASE_URL || env?.VITE_DASHBOARDS_PUBLIC_BASE || "").replace(/\/+$/, "") || window.location.origin;
}
function authHeaders(): Record<string, string> {
  const h: Record<string, string> = { Accept: "application/json" };
  try {
    const raw = localStorage.getItem("auth_payload") || localStorage.getItem("dashboards_auth") || localStorage.getItem("auth");
    if (raw) { const a = JSON.parse(raw); if (a?.access_token) h.Authorization = `Bearer ${a.access_token}`; if (a?.iin) h["X-User-IIN"] = a.iin; }
  } catch { /* ignore */ }
  const role = (localStorage.getItem("dashboards_role") || "").trim();
  if (role) h["X-Role"] = role;
  return h;
}
async function api<T>(path: string, params: Record<string, any>): Promise<T> {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") qs.set(k, String(v)); });
  const res = await fetch(`${apiBase()}${path}?${qs.toString()}`, { headers: authHeaders(), credentials: "omit" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as T;
}
// Роль нужна, чтобы показывать ввод численности только руководству больницы.
function userRole(): string {
  return (localStorage.getItem("dashboards_role") || "").trim().toUpperCase();
}
function canEditPopulation(): boolean {
  return ["DIRECTOR", "SUPERADMIN"].includes(userRole());
}
async function apiPut<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${apiBase()}${path}`, {
    method: "PUT", credentials: "omit",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as T;
}

type OrgOption = { id: string; label: string };
async function fetchOrgs(): Promise<OrgOption[]> {
  const res = await fetch(`/api/v1/analytics/dicts`, { headers: authHeaders(), credentials: "omit" });
  if (!res.ok) throw new Error(`${res.status}`);
  const data = (await res.json()) as { orgs?: Array<Record<string, unknown>> };
  return (Array.isArray(data?.orgs) ? data.orgs : [])
    .map((o) => ({ id: String(o?.orgId || "").trim(), label: String(o?.name || o?.orgName || o?.shortName || o?.orgId || "").trim() }))
    .filter((o) => o.id);
}

// ── типы ответов ──
type Summary = { period: { from: string; to: string }; kpi: {
  population: { total: number; urban: number; rural: number };
  on_du: { total: number; male: number; female: number };
  coverage_pct: number | null; overdue: number;
  movement: { taken: number; removed: number }; no_show_pct: number;
  not_filled?: number;
}};
type Widgets = {
  period: { from: string; to: string };
  w1_plan: { planned: number; passed: number; not_came: number; pct: number; weeks: { week: string; planned: number; passed: number }[] };
  w2_overdue: { area: string; overdue: number }[];
  w3_noshow: { not_come: number; hospitalized: number; moved: number; died: number };
  w4_ages: Record<string, number>;
  w5_areas: { area: string; on_du: number; population: number; coverage_pct: number | null }[];
  w6_doctors: { post_id: string; fio: string; visits: number; areas: number }[];
  w6_doctors_total?: number;
  w6_totals: { visits_per_day: number; areas: number; per_area: number };
  w7_nosology: { group: string; count: number }[];
  w8_movement: { month: number; year?: number; taken: number; removed: number; on_du_end: number; coverage_pct: number | null }[];
  w9_critical: { overdue_in_7d: number; over_6m: number; overdue_middle?: number; hospitalized_now: number; died_period: number };
  w10_quality: {
    total: number; no_event_date: number; pct: number; doctors_total?: number;
    doctors: { post_id: string; fio: string; total: number; no_event_date: number; no_visit_date: number; no_result: number }[];
  };
};
type Bootstrap = { areas: string[]; nosology_groups: string[]; doctors: { post_id: string; fio: string }[] };

const MONTHS = ["Янв", "Фев", "Мар", "Апр", "Май", "Июн", "Июл", "Авг", "Сен", "Окт", "Ноя", "Дек"];

// ── форматирование (ТЗ §7): разряды через NBSP, проценты с запятой ──
const NBSP = " ";
function fmt(n: number | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return "—";
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
}
function fmtPct(v: number | null | undefined): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  return v.toFixed(1).replace(".", ",") + "%";
}

// ── глобальный CSS (адаптив, скелетоны, ховеры) ──
const CSS = `
.duchet-kpi { display:grid; grid-template-columns:repeat(6,1fr); gap:8px; margin-bottom:9px; }
.duchet-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:9px; align-items:stretch; }
.duchet-col { display:flex; flex-direction:column; gap:9px; }
.duchet-col > .duchet-widget { flex:1 1 0; display:flex; flex-direction:column; }
/* ряд «диаграмма + подписи справа» тянется на всю высоту карточки.
   Направление НЕ переопределяем: иначе подписи уезжают под диаграмму. */
.duchet-col > .duchet-widget > .duchet-chartrow { flex:1 1 auto; min-height:0; }
@media (max-width:1280px) {
  .duchet-kpi { grid-template-columns:repeat(3,1fr); }
  .duchet-grid { grid-template-columns:repeat(2,1fr); }
}
@media (max-width:900px) {
  .duchet-kpi { grid-template-columns:repeat(2,1fr); }
  .duchet-grid { grid-template-columns:1fr; }
}
.duchet-exp { position:absolute; top:9px; right:9px; font-size:13px; color:${D.hint}; cursor:pointer; background:none; border:none; padding:2px; line-height:1; }
.duchet-exp:hover { color:${D.axisVal}; }
.duchet-drillable { cursor:pointer; transition:filter .12s; }
.duchet-drillable:hover { filter:brightness(1.14); }
.duchet-row-drill { cursor:pointer; }
.duchet-row-drill:hover { background:rgba(255,255,255,0.06); }
@keyframes duchet-shimmer { 0% { opacity:.45; } 50% { opacity:.9; } 100% { opacity:.45; } }
.duchet-skel { animation:duchet-shimmer 1.2s ease-in-out infinite; background:${W.track}; border-radius:6px; }
@keyframes duchet-pop { from { opacity:0; transform:scale(.97); } to { opacity:1; transform:scale(1); } }
.duchet-full-card { animation:duchet-pop .2s ease; }
.duchet-widget { position:relative; background:${W.card}; border:0.5px solid ${W.cardBorder}; border-radius:10px; padding:11px; }
/* Только ширина: без min-width:0 flex-элемент не сжимается ниже своего содержимого,
   поэтому панель фильтров вылезала за правый край. Высоту и шрифт не трогаем. */
.duchet-select { height:34px; overflow:hidden; white-space:nowrap; border-radius:8px; border:1px solid rgba(255,255,255,0.3); background:${SHELL.btnBg}; color:#fff; padding:0 8px; font-size:13px; flex:0 1 auto; min-width:0; max-width:180px; text-overflow:ellipsis; }
.duchet-btn { height:34px; border-radius:8px; border:1px solid rgba(255,255,255,0.3); background:${SHELL.btnBg}; color:#fff; padding:0 12px; cursor:pointer; font-size:13px; }
/* Прокрутка в тон тёмной теме и с зазором от цифр правой колонки.
   Системная светлая полоса прижималась вплотную к числам и била по глазам. */
.duchet-scroll { scrollbar-width:thin; scrollbar-color:rgba(255,255,255,0.22) transparent; padding-right:16px; }
.duchet-scroll::-webkit-scrollbar { width:9px; }
.duchet-scroll::-webkit-scrollbar-track { background:transparent; }
.duchet-scroll::-webkit-scrollbar-thumb { background:rgba(255,255,255,0.18); border-radius:6px; border:2px solid transparent; background-clip:content-box; }
.duchet-scroll::-webkit-scrollbar-thumb:hover { background:rgba(255,255,255,0.32); border:2px solid transparent; background-clip:content-box; }
.duchet-scroll-thin { scrollbar-width:thin; scrollbar-color:rgba(255,255,255,0.22) transparent; }
.duchet-scroll-thin::-webkit-scrollbar { width:9px; height:9px; }
.duchet-scroll-thin::-webkit-scrollbar-track { background:transparent; }
.duchet-scroll-thin::-webkit-scrollbar-thumb { background:rgba(255,255,255,0.18); border-radius:6px; border:2px solid transparent; background-clip:content-box; }
.duchet-scroll-thin::-webkit-scrollbar-thumb:hover { background:rgba(255,255,255,0.32); border:2px solid transparent; background-clip:content-box; }
.duchet-full-card { scrollbar-width:thin; scrollbar-color:rgba(255,255,255,0.22) transparent; }
.duchet-full-card::-webkit-scrollbar { width:9px; }
.duchet-full-card::-webkit-scrollbar-track { background:transparent; }
.duchet-full-card::-webkit-scrollbar-thumb { background:rgba(255,255,255,0.18); border-radius:6px; border:2px solid transparent; background-clip:content-box; }
`;
function GlobalCss(): JSX.Element { return <style>{CSS}</style>; }

// ── заголовок виджета (полоска слева — тематика, ТЗ §1.3) ──
function WTitle({ stripe, children }: { stripe: string; children: React.ReactNode }) {
  return (
    <div style={{ fontSize: 10.5, color: D.widgetTitle, marginBottom: 9, fontWeight: 500,
      borderLeft: `3px solid ${stripe}`, paddingLeft: 7, textTransform: "uppercase", letterSpacing: 0.3 }}>
      {children}
    </div>
  );
}

// ── состояния (ТЗ §7) ──
function Skel({ h }: { h: number }) { return <div className="duchet-skel" style={{ height: h, width: "100%" }} />; }
function NoData(): JSX.Element {
  return <div style={{ color: D.axis, fontSize: 12, padding: "22px 0", textAlign: "center" }}>Нет данных за выбранный период</div>;
}
function ErrRetry({ msg, onRetry }: { msg: string; onRetry: () => void }) {
  return (
    <div style={{ color: D.redText, fontSize: 12, padding: "16px 0", textAlign: "center" }}>
      Ошибка загрузки: {msg}
      <div><button className="duchet-btn" style={{ marginTop: 8, height: 28 }} onClick={onRetry}>Повторить</button></div>
    </div>
  );
}

// ── разворот виджета на весь экран (ТЗ §5): единая обёртка ──
// Разворачиваются ГРАФИЧЕСКИЕ виджеты; плашки с цифрами — expandable={false} (уточнение заказчика 18.07).
function WidgetShell({ title, stripe, drill, expandable = true, children }: {
  title: string; stripe: string; drill?: () => void; expandable?: boolean;
  children: (expanded: boolean) => React.ReactNode;
}) {
  const [full, setFull] = useState(false);
  useEffect(() => {
    if (!full) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setFull(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [full]);

  const inner = (expanded: boolean) => (
    <>
      <WTitle stripe={stripe}>{title}</WTitle>
      {expandable && (
        <button className="duchet-exp" title={expanded ? "Свернуть (Esc)" : "На весь экран"}
          onClick={() => setFull(!expanded)} style={expanded ? { fontSize: 18, top: 12, right: 12 } : undefined}>
          <i className={`ti ${expanded ? "ti-x" : "ti-maximize"}`} />
        </button>
      )}
      {drill && (
        <button className="duchet-exp" title="Таблица пациентов" onClick={drill}
          style={{ right: !expandable ? 9 : expanded ? 44 : 32, top: expanded ? 12 : 9, fontSize: expanded ? 18 : 13 }}>
          <i className="ti ti-table" />
        </button>
      )}
      {expanded ? (
        <div className="duchet-scroll" style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column",
          // safe center: центрируем, пока контент влезает; если выше экрана — прижимаем
          // к верху, иначе верхние строки уезжают за границу скролла и их не долистать
          justifyContent: "safe center", overflowY: "auto", overflowX: "hidden",
          maxWidth: 1600, width: "100%", margin: "0 auto" }}>
          {children(true)}
        </div>
      ) : children(false)}
    </>
  );

  return (
    <>
      <section className="duchet-widget">{inner(false)}</section>
      {full && (
        <>
          <div onClick={() => setFull(false)}
            style={{ position: "fixed", inset: 0, background: "rgba(4,10,24,0.7)", zIndex: 14000 }} />
          <section className="duchet-widget duchet-full-card"
            style={{ position: "fixed", inset: "4%", width: "92%", height: "92%", zIndex: 14001,
              overflow: "auto", padding: 18, boxSizing: "border-box", display: "flex", flexDirection: "column" }}>
            {inner(true)}
          </section>
        </>
      )}
    </>
  );
}

// ═══ SVG-графики: viewBox => корректный ресайз при развороте (ТЗ §5 «КРИТИЧНО») ═══

// кольцо-gauge (виджет 1)
function RingGauge({ pct, size }: { pct: number; size: number | string }) {
  const r = 33, cx = 44, cy = 44, circ = 2 * Math.PI * r;
  const fill = Math.max(0, Math.min(1, pct / 100)) * circ;
  return (
    <svg viewBox="0 0 88 88" style={{ width: size, height: size, flexShrink: 0 }}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={W.track} strokeWidth={10} />
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={D.green} strokeWidth={10}
        strokeDasharray={`${fill} ${circ}`} strokeLinecap="round" transform={`rotate(-90 ${cx} ${cy})`} />
      <text x={cx} y={41} textAnchor="middle" fill={D.greenText} fontSize={18} fontWeight={500}>{fmtPct(pct).replace(",0%", "%")}</text>
      <text x={cx} y={55} textAnchor="middle" fill={D.axis} fontSize={7.5}>выполнено</text>
    </svg>
  );
}

// donut c кликабельными сегментами (виджеты 2, 3, 7)
type DonutSeg = { label: string; value: number; color: string; onClick?: () => void };
function Donut({ segs, size, center, centerSub }: { segs: DonutSeg[]; size: number | string; center?: string; centerSub?: string }) {
  const r = 30, cx = 41, cy = 41, circ = 2 * Math.PI * r;
  const total = segs.reduce((s, x) => s + x.value, 0);
  let acc = 0;
  return (
    <svg viewBox="0 0 82 82" style={{ width: size, height: size, flexShrink: 0 }}>
      {total <= 0 && <circle cx={cx} cy={cy} r={r} fill="none" stroke={W.track} strokeWidth={14} />}
      {segs.map((s, i) => {
        if (s.value <= 0 || total <= 0) return null;
        const len = (s.value / total) * circ;
        const off = -acc; acc += len;
        return (
          <circle key={i} cx={cx} cy={cy} r={r} fill="none" stroke={s.color} strokeWidth={14}
            strokeDasharray={`${len} ${circ}`} strokeDashoffset={off} transform={`rotate(-90 ${cx} ${cy})`}
            className={s.onClick ? "duchet-drillable" : undefined} onClick={s.onClick}>
            <title>{`${s.label}: ${fmt(s.value)} (${total ? ((s.value / total) * 100).toFixed(1).replace(".", ",") : 0}%)`}</title>
          </circle>
        );
      })}
      {center && <text x={cx} y={38} textAnchor="middle" fill="#fff" fontSize={15} fontWeight={500}>{center}</text>}
      {centerSub && <text x={cx} y={50} textAnchor="middle" fill={D.axis} fontSize={7}>{centerSub}</text>}
    </svg>
  );
}

// строка легенды «● Название .......... Число»
function LegendRow({ color, label, value, valueColor, bold, onClick }: {
  color: string; label: string; value: React.ReactNode; valueColor?: string; bold?: boolean; onClick?: () => void;
}) {
  return (
    <div className={onClick ? "duchet-row-drill" : undefined} onClick={onClick}
      style={{ display: "flex", justifyContent: "space-between", gap: 8, borderRadius: 4, padding: "0 2px" }}>
      <span style={{ color: D.label }}><span style={{ color }}>●</span> {label}</span>
      <span style={{ color: valueColor || SHELL.text, fontWeight: bold ? 500 : undefined }}>{value}</span>
    </div>
  );
}

// мини-график динамики по неделям (виджет 1, низ)
function MiniLine({ points, id, tall }: { points: number[]; id: string; tall?: boolean }) {
  if (points.length < 2) return <NoData />;
  const VW2 = 250, H = 34, pad = 8;
  const max = Math.max(...points, 1), min = Math.min(...points, 0);
  const span = Math.max(1, max - min);
  const xs = points.map((_, i) => pad + (i * (VW2 - 2 * pad)) / (points.length - 1));
  const ys = points.map((v) => H - 6 - ((v - min) / span) * (H - 12));
  const poly = xs.map((x, i) => `${x},${ys[i]}`).join(" ");
  const path = `M${xs[0]},${ys[0]} ` + xs.slice(1).map((x, i) => `L${x},${ys[i + 1]}`).join(" ") + ` L${xs[xs.length - 1]},${H} L${xs[0]},${H} Z`;
  return (
    <svg viewBox={`0 0 ${VW2} ${H}`} style={{ width: "100%", height: tall ? "16vh" : H }} preserveAspectRatio="none">
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={D.green} stopOpacity={0.35} />
          <stop offset="1" stopColor={D.green} stopOpacity={0} />
        </linearGradient>
      </defs>
      <path d={path} fill={`url(#${id})`} />
      <polyline points={poly} fill="none" stroke={D.green} strokeWidth={2} />
    </svg>
  );
}

// вертикальные столбцы возрастов (виджет 4)
function AgeBars({ data, onClick, expanded }: {
  data: { label: string; value: number; color: string }[];
  onClick?: (label: string) => void; expanded: boolean;
}) {
  const VW = 260, H = expanded ? 150 : 104, axisY = H - 18;
  const max = Math.max(...data.map((d) => d.value), 1);
  const bw = 34, gap = (VW - 16 - data.length * bw) / Math.max(1, data.length - 1);
  return (
    <svg viewBox={`0 0 ${VW} ${H}`} preserveAspectRatio="xMidYMid meet"
      style={{ width: "100%", height: "auto", maxHeight: expanded ? "min(66vh, 620px)" : "none" }}>
      <line x1={8} y1={axisY} x2={VW - 8} y2={axisY} stroke={W.divider} strokeWidth={1} />
      {data.map((d, i) => {
        const h = Math.max(2, (d.value / max) * (axisY - 22));
        const x = 8 + 14 + i * (bw + gap) - 14;
        return (
          <g key={d.label} className={onClick ? "duchet-drillable" : undefined} onClick={onClick ? () => onClick(d.label) : undefined}>
            <title>{`${d.label}: ${fmt(d.value)}`}</title>
            <rect x={x} y={axisY - h} width={bw} height={h} fill={d.color} rx={2} />
            <text x={x + bw / 2} y={axisY - h - 4} textAnchor="middle" fill={D.axisVal} fontSize={9}>{fmt(d.value)}</text>
            <text x={x + bw / 2} y={H - 6} textAnchor="middle" fill={D.axis} fontSize={8}>{d.label}</text>
          </g>
        );
      })}
    </svg>
  );
}

// инлайн-бар для таблиц рейтинга (виджеты 5, 6)
function InlineBar({ frac, color, h }: { frac: number; color: string; h: number }) {
  return (
    <div style={{ background: W.track, borderRadius: 2, height: h, width: "100%" }}>
      <div style={{ width: `${Math.max(0, Math.min(100, frac * 100))}%`, background: color, height: h, borderRadius: 2 }} />
    </div>
  );
}

// ── KPI-карточка (ТЗ §3) ──
function KpiCard({ label, icon, value, sub, tone, onClick }: {
  label: string; icon: string; value: React.ReactNode; sub: string; tone: ToneKey; onClick?: () => void;
}) {
  const t = TONES[tone];
  return (
    <div className={onClick ? "duchet-drillable" : undefined} onClick={onClick}
      style={{ background: t.bg, border: `0.5px solid ${t.border}`, borderRadius: 9, padding: "9px 10px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 9.5, color: t.label, textTransform: "uppercase", letterSpacing: 0.3, fontWeight: 500 }}>{label}</span>
        <i className={`ti ${icon}`} style={{ fontSize: 14, color: t.accent }} />
      </div>
      <div style={{ fontSize: 20, fontWeight: 500, color: "#fff", marginTop: 2 }}>{value}</div>
      <div style={{ fontSize: 9, color: t.sub, marginTop: 1 }}>{sub}</div>
    </div>
  );
}

// ── подписи для страницы drill-таблицы ──
const DRILL_TITLES: Record<string, string> = {
  on_du: "Состоит на ДУ", overdue: "Просроченные осмотры", noshow: "Неявка на осмотры",
  age: "Возрастная группа", area: "Пациенты участка", doctor: "Пациенты врача", nosology: "Группа диагноза",
  overdue7: "Станут просроченными за 7 дней", over6m: "Просрочка более 6 месяцев",
  hosp_now: "Госпитализированы сейчас", died: "Умерли за период", movement: "Движение ДУ за период",
  missing_event_date: "Визиты без даты записи",
  doctor_visits: "Визиты врача",
};
const NOSHOW_LABELS: Record<string, string> = {
  not_come: "Не пришёл", hospitalized: "Госпитализирован", moved: "Сменил адрес / снят", died: "Умер",
};
function bucketLabel(drill: string, bucket: string): string {
  if (!bucket) return "";
  if (drill === "noshow") return NOSHOW_LABELS[bucket] || bucket;
  if (drill === "movement") {
    const [kind, mon] = bucket.split(":");
    const m = MONTHS[Number(mon) - 1] || "";
    return kind === "taken" ? `взято за ${m}` : `снято за ${m}`;
  }
  return bucket;
}

// ═══════════════════════ СТРАНИЦА ═══════════════════════
export default function DuchetDashboardPage(): JSX.Element {
  const qs = new URLSearchParams(window.location.search);
  const now = new Date();

  const [popOpen, setPopOpen] = useState(false);
  // фильтры (7 существующих + календарь-диапазон, ТЗ §2 п.2)
  const [misId, setMisId] = useState<string>(qs.get("mis_id") || "");
  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [year, setYear] = useState<number>(Number(qs.get("year")) || now.getFullYear());
  const [month, setMonth] = useState<number>(Number(qs.get("month")) || 0);
  const [dateFrom, setDateFrom] = useState<string>(qs.get("date_from") || "");
  const [dateTo, setDateTo] = useState<string>(qs.get("date_to") || "");
  const [flt, setFlt] = useState<{ area?: string; doctor?: string; nosology?: string; sex?: string; rural?: string; age_from?: string; age_to?: string }>({
    area: qs.get("area") || undefined, doctor: qs.get("doctor") || undefined,
    nosology: qs.get("nosology") || undefined, sex: qs.get("sex") || undefined, rural: qs.get("rural") || undefined,
    age_from: qs.get("age_from") || undefined, age_to: qs.get("age_to") || undefined,
  });
  // возраст занимал две плашки; теперь одна, значения выбираются во всплывающей панельке
  const [ageOpen, setAgeOpen] = useState(false);

  const [boot, setBoot] = useState<Bootstrap | null>(null);
  const [sum, setSum] = useState<Summary | null>(null);
  const [wg, setWg] = useState<Widgets | null>(null);
  const [sumErr, setSumErr] = useState("");
  const [wgErr, setWgErr] = useState("");
  const [sumLoading, setSumLoading] = useState(true);
  const [wgLoading, setWgLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  const params = useMemo(() => ({
    mis_id: misId, year, month: month || undefined,
    date_from: dateFrom || undefined, date_to: dateTo || undefined,
    area: flt.area, doctor: flt.doctor, nosology: flt.nosology, sex: flt.sex,
    rural: flt.rural === "" || flt.rural === undefined ? undefined : flt.rural,
    // возраст: пустая строка и мусор не отправляются, иначе бэкенд получит NaN
    age_from: Number.isFinite(Number(flt.age_from)) && flt.age_from ? Number(flt.age_from) : undefined,
    age_to: Number.isFinite(Number(flt.age_to)) && flt.age_to ? Number(flt.age_to) : undefined,
  }), [misId, year, month, dateFrom, dateTo, flt]);

  // организации пользователя (как на остальных дашбордах)
  useEffect(() => {
    fetchOrgs().then((list) => {
      setOrgs(list);
      if (!misId && list.length) setMisId(list[0].id);
    }).catch(() => { /* селектор останется пустым, mis из URL */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!misId) return;
    api<Bootstrap>("/api/v1/duchet/dashboard/bootstrap", { mis_id: misId }).then(setBoot).catch(() => {});
  }, [misId]);

  // данные — независимо KPI и виджеты (виджеты появляются по мере готовности, ТЗ §7)
  useEffect(() => {
    if (!misId) return;
    setSumLoading(true); setSumErr("");
    api<Summary>("/api/v1/duchet/dashboard/summary", params)
      .then(setSum).catch((e) => setSumErr(String(e?.message || e))).finally(() => setSumLoading(false));
  }, [params, misId, reloadKey]);
  useEffect(() => {
    if (!misId) return;
    setWgLoading(true); setWgErr("");
    api<Widgets>("/api/v1/duchet/dashboard/widgets", params)
      .then(setWg).catch((e) => setWgErr(String(e?.message || e))).finally(() => setWgLoading(false));
  }, [params, misId, reloadKey]);

  // drill-down: универсальный конфиг — контекст {drill, bucket} + фильтры => новое окно (ТЗ §6)
  const openCards = useCallback((drill: string, bucket?: string) => {
    const p = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") p.set(k, String(v)); });
    p.set("drill", drill);
    if (bucket) p.set("bucket", bucket);
    window.open(`${publicBase()}/dashboards/duchet/cards?${p.toString()}`, "_blank", "noopener,noreferrer");
  }, [params]);

  const resetAll = () => {
    setMonth(0); setDateFrom(""); setDateTo(""); setFlt({}); setYear(now.getFullYear());
  };

  const kpi = sum?.kpi;
  // охват может не считаться (фильтр по врачу/нозологии/возрасту) — тогда нейтральный тон
  const covTone: ToneKey = kpi && kpi.coverage_pct !== null ? planToneForCoverage(kpi.coverage_pct) : "yellow";

  // светофор охвата для KPI-3: относительно порогов плана не применим — используем участковый принцип:
  // зелёный если охват >= среднего*1.1 невозможно на уровне орг, поэтому: >=15% зелёный, >=10% жёлтый, ниже — красный.
  function planToneForCoverage(pct: number): ToneKey {
    if (pct >= 15) return "green";
    if (pct >= 10) return "yellow";
    return "red";
  }

  const avgCoverage = useMemo(() => {
    const rows = (wg?.w5_areas || []).filter((r) => r.population > 0);
    if (!rows.length) return 0;
    const withCov = rows.filter((r) => r.coverage_pct !== null);
    if (!withCov.length) return 0;
    return withCov.reduce((s, r) => s + (r.coverage_pct as number), 0) / withCov.length;
  }, [wg]);


  return (
    <div style={{ minHeight: "100vh", background: SHELL.page, padding: 10, color: SHELL.text,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <GlobalCss />
      <div style={{ maxWidth: 1400, margin: "0 auto" }}>

        {/* ── ШАПКА + ПАНЕЛЬ ФИЛЬТРОВ: стиль существующих дашбордов ── */}
        <div style={{ background: SHELL.card, borderRadius: 12, border: `1px solid ${SHELL.border}`, padding: 12, marginBottom: 10 }}>
          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "nowrap", minWidth: 0 }}>
            <select className="duchet-select" title="Подразделение" value={misId} onChange={(e) => setMisId(e.target.value)}>
              {!orgs.length && <option value={misId}>{misId || "—"}</option>}
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>

            <select className="duchet-select" style={{ width: 76, flex: "0 0 76px" }} title="Год" value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {[0, 1, 2, 3].map((d) => { const y = now.getFullYear() - d; return <option key={y} value={y}>{y}</option>; })}
            </select>
            <select className="duchet-select" style={{ width: 96, flex: "0 0 96px" }} title="Месяц" value={month} onChange={(e) => setMonth(Number(e.target.value))}>
              <option value={0}>Месяц: весь год</option>
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>

            {/* календарь-диапазон — единый, как в остальных дашбордах */}
            <PeriodCalendar
              dateFrom={dateFrom || undefined}
              dateTo={dateTo || undefined}
              onApply={(from, to) => { setDateFrom(from); setDateTo(to); }}
              onClear={() => { setDateFrom(""); setDateTo(""); }}
            />

            <select className="duchet-select" style={{ width: 108, flex: "0 0 108px" }} title="Участок" value={flt.area || ""} onChange={(e) => setFlt({ ...flt, area: e.target.value || undefined })}>
              <option value="">Участок</option>
              {(boot?.areas || []).map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
            <select className="duchet-select" style={{ width: 116, flex: "0 0 116px" }} title="Нозология" value={flt.nosology || ""} onChange={(e) => setFlt({ ...flt, nosology: e.target.value || undefined })}>
              <option value="">Нозология</option>
              {(boot?.nosology_groups || []).map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
            <select className="duchet-select" style={{ width: 92, flex: "0 0 92px" }} title="Врач" value={flt.doctor || ""} onChange={(e) => setFlt({ ...flt, doctor: e.target.value || undefined })}>
              <option value="">Врачи</option>
              {(boot?.doctors || []).map((d) => <option key={d.post_id} value={d.post_id}>{d.fio}</option>)}
            </select>
            <select className="duchet-select" style={{ width: 72, flex: "0 0 72px" }} title="Пол" value={flt.sex || ""} onChange={(e) => setFlt({ ...flt, sex: e.target.value || undefined })}>
              <option value="">Пол</option><option value="М">М</option><option value="Ж">Ж</option>
            </select>
            <select className="duchet-select" style={{ width: 112, flex: "0 0 112px" }} title="Город или село" value={flt.rural ?? ""} onChange={(e) => setFlt({ ...flt, rural: e.target.value || undefined })}>
              <option value="">Город/село</option><option value="false">Город</option><option value="true">Село</option>
            </select>

            <div style={{ position: "relative" }}>
              <button className="duchet-select" style={{ maxWidth: 104, cursor: "pointer", textAlign: "left" }}
                title="Возраст пациента" onClick={() => setAgeOpen((v) => !v)}>
                <i className="ti ti-user" style={{ marginRight: 5, opacity: 0.75 }} />
                {flt.age_from || flt.age_to
                  ? (flt.age_from && flt.age_to ? `${flt.age_from}\u2013${flt.age_to}`
                     : flt.age_from ? `от ${flt.age_from}` : `до ${flt.age_to}`)
                  : "Возраст"}
              </button>
              {ageOpen && (
                <div style={{
                  position: "absolute", top: 38, left: 0, zIndex: 40, padding: 10, borderRadius: 10,
                  background: SHELL.card, border: `1px solid ${SHELL.border}`,
                  boxShadow: "0 8px 24px rgba(0,0,0,0.35)", display: "flex", flexDirection: "column", gap: 8,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <input className="duchet-select" style={{ width: 66 }} inputMode="numeric" placeholder="от"
                      value={flt.age_from || ""} autoFocus
                      onChange={(e) => setFlt({ ...flt, age_from: e.target.value || undefined })} />
                    <span style={{ color: D.axis }}>&ndash;</span>
                    <input className="duchet-select" style={{ width: 66 }} inputMode="numeric" placeholder="до"
                      value={flt.age_to || ""}
                      onChange={(e) => setFlt({ ...flt, age_to: e.target.value || undefined })} />
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button className="duchet-btn" style={{ padding: "0 8px", fontSize: 12 }}
                      onClick={() => setFlt({ ...flt, age_from: undefined, age_to: "17" })}>Дети</button>
                    <button className="duchet-btn" style={{ padding: "0 8px", fontSize: 12 }}
                      onClick={() => setFlt({ ...flt, age_from: "18", age_to: undefined })}>Взрослые</button>
                    <button className="duchet-btn" style={{ padding: "0 8px", fontSize: 12 }}
                      onClick={() => { setFlt({ ...flt, age_from: undefined, age_to: undefined }); setAgeOpen(false); }}>Сброс</button>
                  </div>
                  <button className="duchet-btn" style={{ padding: "0 8px", fontSize: 12 }}
                    onClick={() => setAgeOpen(false)}>Готово</button>
                </div>
              )}
            </div>

            <button className="duchet-btn" style={{ padding: 0, width: 34, minWidth: 34, flex: "0 0 34px" }} title="Сбросить все фильтры" onClick={resetAll}><i className="ti ti-filter-off" /></button>
            <button className="duchet-btn" style={{ padding: 0, width: 34, minWidth: 34, flex: "0 0 34px" }} title="Обновить данные" onClick={() => setReloadKey((k) => k + 1)}><i className="ti ti-refresh" /></button>
            {canEditPopulation() && (
              <button className="duchet-btn" style={{ padding: 0, width: 34, minWidth: 34, flex: "0 0 34px" }}
                title="Внести численность прикреплённого населения (только руководство)"
                onClick={() => setPopOpen(true)}>
                <i className="ti ti-pencil-plus" />
              </button>
            )}

            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6 }}>
              {/* стрелки по закладкам: Д-учёт — последняя закладка дашбордов стационара */}
              <button className="duchet-btn" style={{ padding: "0 10px" }} title="Назад: Dashboards стационар"
                onClick={() => {
                  const mis = (misId || "").trim();
                  window.location.href = "/dashboards/hospital?place=%D0%94%D0%A1&page=last" + (mis ? "&mis_id=" + encodeURIComponent(mis) : "");
                }}>
                {"<<"}
              </button>
              <button className="duchet-btn" style={{ padding: "0 10px" }} title="Вперёд: Вакцинация"
                onClick={() => {
                  const mis = (misId || "").trim();
                  window.location.href = "/dashboards/vaccination" + (mis ? "?mis_id=" + encodeURIComponent(mis) : "");
                }}>
                {">>"}
              </button>
              <details style={{ position: "relative" }}>
                <summary style={{ color: "#fff", cursor: "pointer", listStyle: "none", fontSize: 18, padding: "0 4px" }}>☰</summary>
                <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", background: SHELL.page,
                  border: `1px solid ${SHELL.border}`, borderRadius: 8, padding: 8, display: "flex",
                  flexDirection: "column", gap: 6, zIndex: 20000, minWidth: 220 }}>
                  {[
                    { label: "Главная", href: "/" },
                    { label: "Dashboards АПП", href: "/dashboards/app" },
                    { label: "Dashboards КС", href: "/dashboards/hospital?place=%D0%9A%D0%A1" },
                    { label: "Охрана материнства", href: "/dashboards/mother-hospital" },
                    { label: "Dashboards ДС", href: "/dashboards/hospital?place=%D0%94%D0%A1" },
                    { label: "Ситуационный центр", href: "/dashboards/situation-center" },
                    { label: "Диспансерный учёт", href: "/dashboards/duchet" },
                    { label: "Вакцинация", href: "/dashboards/vaccination" },
                  ].map((route) => (
                    <a key={route.href} href={route.href} target="_blank" rel="noreferrer"
                      style={{ color: "#fff", padding: "6px 8px", borderRadius: 6, textDecoration: "none" }}>
                      {route.label}
                    </a>
                  ))}
                </div>
              </details>
            </div>
          </div>
        </div>

        {/* ═══ ВНУТРЕННОСТЬ-«РАЗВОРОТ»: панель как в эталонном макете ═══ */}
        <div style={{ background: W.wrap, borderRadius: 14, padding: 13 }}>

        {/* ── KPI-ПАНЕЛЬ: 6 карточек (ТЗ §3) ── */}
        {sumErr ? (
          <div style={{ marginBottom: 9 }}><ErrRetry msg={sumErr} onRetry={() => setReloadKey((k) => k + 1)} /></div>
        ) : (
          <div className="duchet-kpi">
            {sumLoading || !kpi ? (
              Array.from({ length: 6 }, (_, i) => <Skel key={i} h={64} />)
            ) : (
              <>
                <KpiCard label="Население" icon="ti-users" tone="blue"
                  value={fmt(kpi.population.total)}
                  sub={`город ${fmt(kpi.population.urban)} · село ${fmt(kpi.population.rural)}`} />
                <KpiCard label="Состоит на ДУ" icon="ti-clipboard-check" tone="green"
                  value={<span style={{ color: D.greenText }}>{fmt(kpi.on_du.total)}</span>}
                  sub={`М ${fmt(kpi.on_du.male)} · Ж ${fmt(kpi.on_du.female)}`}
                  onClick={() => openCards("on_du")} />
                <KpiCard label="Охват ДУ" icon="ti-chart-pie" tone={covTone}
                  value={<span style={{ color: TONES[covTone].accent }}>{fmtPct(kpi.coverage_pct)}</span>}
                  sub="доля населения" onClick={() => openCards("on_du")} />
                <KpiCard label="Просрочили" icon="ti-clock-exclamation" tone="red"
                  value={<span style={{ color: D.redText }}>{fmt(kpi.overdue)}</span>}
                  sub={`критич. ${fmt(wg?.w9_critical.over_6m ?? 0)}`}
                  onClick={() => openCards("overdue")} />
                <KpiCard label="Движение" icon="ti-arrows-exchange" tone="violet"
                  value={<>+{fmt(kpi.movement.taken)}<span style={{ color: D.redText, fontSize: 15 }}>/−{fmt(kpi.movement.removed)}</span></>}
                  sub="взято / снято" onClick={() => openCards("movement")} />
                <KpiCard label="Не явились" icon="ti-user-off" tone="gray"
                  value={fmt((wg?.w1_plan.not_came ?? Math.round(kpi.no_show_pct)))}
                  sub={`${fmtPct(kpi.no_show_pct)} от плана`} onClick={() => openCards("noshow")} />
              </>
            )}
          </div>
        )}

        {/* ── СЕТКА ВИДЖЕТОВ 3 колонки (ТЗ §2 п.5, §4) ── */}
        {wgErr ? (
          <ErrRetry msg={wgErr} onRetry={() => setReloadKey((k) => k + 1)} />
        ) : (
          <div className="duchet-grid">
            {/* ═ КОЛОНКА 1 ═ */}
            <div className="duchet-col">
              <WidgetShell title="1. Выполнение плана осмотров" stripe={D.green} drill={() => openCards("overdue")}>
                {(exp) => wgLoading || !wg ? <Skel h={170} /> : wg.w1_plan.planned === 0 ? <NoData /> : (
                  <>
                    <div className="duchet-chartrow" style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "nowrap" }}>
                      <RingGauge pct={wg.w1_plan.pct} size={exp ? "min(68vh, 680px)" : "clamp(132px, 19vh, 216px)"} />
                      <div style={{ flex: 1, minWidth: 0, fontSize: exp ? 22 : 14.5, lineHeight: 2.0 }}>
                        <LegendRow color={D.blue} label="План" value={fmt(wg.w1_plan.planned)} />
                        <LegendRow color={D.green} label="Прошли" value={fmt(wg.w1_plan.passed)} valueColor={D.greenText} />
                        <LegendRow color={D.red} label="Не пришли" value={fmt(wg.w1_plan.not_came)} valueColor={D.redText}
                          onClick={() => openCards("noshow")} />
                      </div>
                    </div>
                    <div style={{ borderTop: `0.5px solid ${W.divider}`, marginTop: 8, paddingTop: 7 }}>
                      <div style={{ fontSize: exp ? 15 : 11, color: D.axis, marginBottom: 3 }}>Динамика по неделям</div>
                      <MiniLine points={wg.w1_plan.weeks.map((w) => w.passed)} id={exp ? "duchet-g1x" : "duchet-g1"} tall={exp} />
                    </div>
                  </>
                )}
              </WidgetShell>

              <WidgetShell title="2. Просроченные осмотры" stripe={D.red} drill={() => openCards("overdue")}>
                {(exp) => wgLoading || !wg ? <Skel h={150} /> : !wg.w2_overdue.length ? <NoData /> : (() => {
                  const rows = exp ? wg.w2_overdue : wg.w2_overdue.slice(0, 8);
                  const totalOd = wg.w2_overdue.reduce((s, r) => s + r.overdue, 0);
                  const colorOf = (i: number) => i === 0 ? D.red : i <= 2 ? D.yellow : D.blue;
                  return (
                    <>
                      <div className="duchet-chartrow" style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "nowrap" }}>
                        <Donut size={exp ? "min(68vh, 680px)" : "clamp(132px, 19vh, 216px)"} center={fmt(totalOd)} centerSub="всего"
                          segs={wg.w2_overdue.map((r, i) => ({
                            label: r.area, value: r.overdue, color: colorOf(i),
                            onClick: () => openCards("overdue", r.area),
                          }))} />
                        <div style={{ flex: 1, minWidth: 0, fontSize: exp ? 22 : 14, lineHeight: 1.95 }}>
                          {rows.map((r, i) => (
                            <LegendRow key={r.area} color={colorOf(i)} label={r.area}
                              value={fmt(r.overdue)} valueColor={i === 0 ? D.redText : undefined} bold={i === 0}
                              onClick={() => openCards("overdue", r.area)} />
                          ))}
                        </div>
                      </div>
                      <div className="duchet-drillable" onClick={() => openCards("overdue7")}
                        style={{ background: "#2c1112", border: "0.5px solid #8a2622", borderRadius: 6,
                          padding: "6px 9px", marginTop: 8, fontSize: exp ? 14 : 11, color: "#f0a3a3", flex: "0 0 auto" }}>
                        <i className="ti ti-alarm" style={{ fontSize: 12, verticalAlign: -2 }} /> За 7 дней добавится:{" "}
                        <b style={{ color: D.redText }}>{fmt(wg.w9_critical.overdue_in_7d)}</b>
                      </div>
                    </>
                  );
                })()}
              </WidgetShell>

              <WidgetShell title="3. Неявка — причины" stripe={D.violet} drill={() => openCards("noshow")}>
                {(exp) => wgLoading || !wg ? <Skel h={110} /> : (() => {
                  const n = wg.w3_noshow;
                  const totalN = n.not_come + n.hospitalized + n.moved + n.died;
                  if (!totalN) return <NoData />;
                  const segs: DonutSeg[] = [
                    { label: "Не пришёл", value: n.not_come, color: D.blue, onClick: () => openCards("noshow", "not_come") },
                    { label: "Госпитализирован", value: n.hospitalized, color: D.yellow, onClick: () => openCards("noshow", "hospitalized") },
                    { label: "Сменил адрес / снят", value: n.moved, color: D.green, onClick: () => openCards("noshow", "moved") },
                    { label: "Умер", value: n.died, color: D.gray, onClick: () => openCards("noshow", "died") },
                  ];
                  return (
                    <div className="duchet-chartrow" style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "nowrap" }}>
                      <Donut size={exp ? "min(68vh, 680px)" : "clamp(132px, 19vh, 216px)"} segs={segs} />
                      <div style={{ flex: 1, minWidth: 0, fontSize: exp ? 22 : 14.5, lineHeight: 2.0 }}>
                        {segs.map((s) => (
                          <LegendRow key={s.label} color={s.color} label={s.label} value={fmt(s.value)} onClick={s.onClick} />
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </WidgetShell>
            </div>

            {/* ═ КОЛОНКА 2 ═ */}
            <div className="duchet-col">
              <WidgetShell title="4. Возрастная структура ДУ" stripe={D.blue}>
                {(exp) => wgLoading || !wg ? <Skel h={110} /> : (() => {
                  // «не определён» — люди с невалидным ИИН: раньше они молча уходили
                  // в женщины и в группу 60+, теперь видны отдельно
                  const order = ["0-17", "18-39", "40-59", "60+", "не определён"];
                  const colors = [D.blue, D.blue2, D.blue3, D.blue4];
                  const data = order.map((k, i) => ({ label: k.replace("-", "–"), value: wg.w4_ages[k] ?? 0, color: colors[i] }));
                  if (!data.some((d) => d.value > 0)) return <NoData />;
                  return <AgeBars data={data} expanded={exp}
                    onClick={(label) => openCards("age", label.replace("–", "-"))} />;
                })()}
              </WidgetShell>

              <WidgetShell title="5. Рейтинг участков" stripe={D.green}>
                {(exp) => wgLoading || !wg ? <Skel h={150} /> : !wg.w5_areas.length ? <NoData /> : (() => {
                  // участки без данных о населении (охват null) — в конец списка
                  const rows = [...wg.w5_areas].sort((a, b) =>
                    (a.coverage_pct === null ? 1 : 0) - (b.coverage_pct === null ? 1 : 0)
                    || (b.coverage_pct ?? 0) - (a.coverage_pct ?? 0)
                    || b.on_du - a.on_du);
                  const shown = exp ? rows : rows.slice(0, 9);
                  const maxCov = Math.max(...rows.map((r) => r.coverage_pct ?? 0), 1);
                  return (
                    <>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: exp ? 18 : 12.5 }}>
                        <tbody>
                          {shown.map((r) => {
                            const col = r.coverage_pct === null ? D.hint : areaColor(r.coverage_pct, avgCoverage);
                            return (
                              <tr key={r.area} className="duchet-row-drill" onClick={() => openCards("area", r.area)}>
                                <td style={{ width: 14, padding: "3px 0" }}>
                                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: col, display: "inline-block" }} />
                                </td>
                                <td style={{ color: D.rowText, padding: "3px 4px", whiteSpace: "nowrap" }}>{r.area}</td>
                                <td style={{ width: exp ? 320 : 96, padding: "5px 4px" }}>
                                  <InlineBar frac={(r.coverage_pct ?? 0) / maxCov} color={col} h={8} />
                                </td>
                                <td style={{ textAlign: "right", color: r.coverage_pct === null ? D.hint : col,
                                  fontWeight: 500, padding: "3px 0" }}
                                  title={r.coverage_pct === null
                                    ? `Нет данных о населении участка: прикреплено ${r.population}, на учёте ${r.on_du}`
                                    : undefined}>
                                  {r.coverage_pct === null ? "—" : r.coverage_pct.toFixed(1).replace(".", ",")}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      <div style={{ fontSize: 10.5, color: D.hint, marginTop: 7 }}>
                        <i className="ti ti-click" style={{ fontSize: 10 }} /> клик по строке → пациенты участка
                      </div>
                    </>
                  );
                })()}
              </WidgetShell>

              <WidgetShell title="6. Работа врачей" stripe={D.blue4}>
                {(exp) => wgLoading || !wg ? <Skel h={150} /> : !wg.w6_doctors.length ? <NoData /> : (() => {
                  const rows = exp ? wg.w6_doctors : wg.w6_doctors.slice(0, 8);
                  const maxV = Math.max(...wg.w6_doctors.map((r) => r.visits), 1);
                  const laggard = wg.w6_doctors.length >= 2 ? wg.w6_doctors[wg.w6_doctors.length - 1].post_id : "";
                  return (
                    <>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: exp ? 14 : 10 }}>
                        <tbody>
                          {rows.map((r) => {
                            const lag = r.post_id === laggard;
                            const col = lag ? D.yellow : D.blue;
                            return (
                              <tr key={r.post_id} className="duchet-row-drill"
                                onClick={() => openCards("doctor_visits", r.post_id)}>
                                <td style={{ width: 14, padding: "3px 0" }}>
                                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: col,
                                    display: "inline-block" }} />
                                </td>
                                <td style={{ color: D.rowText, padding: "3px 4px", maxWidth: exp ? 320 : 90,
                                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                  title={r.fio}>{r.fio}</td>
                                <td style={{ width: exp ? 220 : 70, padding: "3px 4px" }}>
                                  <InlineBar frac={r.visits / maxV} color={col} h={8} />
                                </td>
                                <td style={{ textAlign: "right", color: lag ? D.yellow : SHELL.text,
                                  fontWeight: 500, padding: "3px 0" }}>{fmt(r.visits)}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 5, marginTop: 8,
                        borderTop: `0.5px solid ${W.divider}`, paddingTop: 7 }}>
                        <MiniStat value={String(wg.w6_totals.visits_per_day).replace(".", ",")} label="визитов/день" color={D.blue} />
                        <MiniStat value={fmt(wg.w6_totals.areas)} label="участков" color={D.greenText} />
                        <MiniStat value={fmt(wg.w6_totals.per_area)} label="на участок" color={D.violetText} />
                      </div>
                    </>
                  );
                })()}
              </WidgetShell>
            </div>

            {/* ═ КОЛОНКА 3 ═ */}
            <div className="duchet-col">
              <WidgetShell title="7. Структура ДУ по заболеваниям" stripe={D.violet}>
                {(exp) => wgLoading || !wg ? <Skel h={130} /> : !wg.w7_nosology.length ? <NoData /> : (() => {
                  const colors = [D.violet, D.blue, D.green, D.yellow, D.orange, D.gray, D.grayDim, D.blue3];
                  const total = wg.w7_nosology.reduce((s, r) => s + r.count, 0);
                  const rows = exp ? wg.w7_nosology : wg.w7_nosology.slice(0, 6);
                  return (
                    <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
                      <Donut size={exp ? "min(46vh, 440px)" : 76} segs={wg.w7_nosology.map((r, i) => ({
                        label: r.group, value: r.count, color: colors[i % colors.length],
                        onClick: () => openCards("nosology", r.group),
                      }))} />
                      <div style={{ flex: 1, fontSize: exp ? 14 : 9.5, lineHeight: 1.55 }}>
                        {rows.map((r, i) => (
                          <LegendRow key={r.group} color={colors[i % colors.length]} label={r.group}
                            value={total ? ((r.count * 100) / total).toFixed(0) + "%" : "0%"}
                            onClick={() => openCards("nosology", r.group)} />
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </WidgetShell>

              <WidgetShell title="8. Движение ДУ по месяцам" stripe={D.green}>
                {(exp) => wgLoading || !wg ? <Skel h={160} /> : (() => {
                  // бэкенд отдаёт ровно те месяцы, что попали в выбранный период
                  const nowMon = now.getMonth() + 1;
                  const nowYear = now.getFullYear();
                  const rows = wg.w8_movement;
                  if (!rows.some((r) => r.taken || r.removed || r.on_du_end)) return <NoData />;
                  const shown = exp ? rows : rows.slice(-7);
                  return (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: exp ? 14 : 9.5 }}>
                      <thead>
                        <tr style={{ color: "#5a6d84", fontSize: exp ? 12 : 8.5 }}>
                          <td style={{ paddingBottom: 3 }}>Мес.</td>
                          <td style={{ textAlign: "right" }}>Взято</td>
                          <td style={{ textAlign: "right" }}>Снято</td>
                          <td style={{ textAlign: "right" }}>Состоит</td>
                          <td style={{ textAlign: "right" }}>Охв.</td>
                        </tr>
                      </thead>
                      <tbody>
                        {shown.map((r) => {
                          const cur = r.month === nowMon && (r.year ?? nowYear) === nowYear;
                          return (
                            <tr key={`${r.year ?? ""}-${r.month}`} className="duchet-row-drill"
                              onClick={() => openCards("movement", `taken:${r.month}`)}
                              style={{ borderTop: `0.5px solid ${W.rowBorder}`, background: cur ? "#0d1f16" : undefined }}>
                              <td style={{ padding: "2.5px 0", color: cur ? "#fff" : D.rowText }}>{MONTHS[r.month - 1]}</td>
                              <td style={{ textAlign: "right", color: D.greenText }}>{fmt(r.taken)}</td>
                              <td style={{ textAlign: "right", color: D.redText }}
                                onClick={(e) => { e.stopPropagation(); openCards("movement", `removed:${r.month}`); }}>{fmt(r.removed)}</td>
                              <td style={{ textAlign: "right", color: cur ? "#fff" : SHELL.text }}>{fmt(r.on_du_end)}</td>
                              <td style={{ textAlign: "right", color: cur ? D.greenText : D.axisVal, fontWeight: cur ? 500 : undefined }}>
                                {r.coverage_pct === null ? "—" : r.coverage_pct.toFixed(1).replace(".", ",")}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  );
                })()}
              </WidgetShell>

              <WidgetShell title="9. Критические индикаторы" stripe={D.red} expandable={false}>
                {(exp) => wgLoading || !wg ? <Skel h={120} /> : (() => {
                  const c = wg.w9_critical;
                  // «ниже нормы» считаем только там, где охват вообще посчитан
                  const belowNorm = (wg.w5_areas || []).filter((r) => r.coverage_pct !== null
                    && areaColor(r.coverage_pct, avgCoverage) === D.red).length;
                  const cells: { v: string; label: string; bg: string; border: string; num: string; sub: string; drill?: () => void }[] = [
                    { v: fmt(c.overdue_in_7d), label: "просрочат 7 дн.", bg: "#361718", border: "#a3302c", num: D.redText, sub: "#c98a88", drill: () => openCards("overdue7") },
                    { v: fmt(c.over_6m), label: ">6 мес", bg: "#33280c", border: "#97710f", num: D.yellow, sub: "#bd9a54", drill: () => openCards("over6m") },
                    { v: fmt(c.overdue_middle ?? 0), label: "просрочено до 6 мес", bg: "#33280c", border: "#97710f", num: D.yellow, sub: "#bd9a54" },
                    { v: fmt(belowNorm), label: "уч. ниже нормы", bg: "#361718", border: "#a3302c", num: D.redText, sub: "#c98a88" },
                    { v: fmt(c.hospitalized_now), label: "госпитализ.", bg: "#0e2540", border: "#2f6fb0", num: D.blue, sub: "#7ba3cd", drill: () => openCards("hosp_now") },
                    { v: fmt(c.died_period), label: "умерло", bg: "#1a2231", border: "#3a4657", num: D.grayText, sub: "#83868f", drill: () => openCards("died") },
                    { v: fmtPct(wg.w1_plan.pct), label: "вып. плана", bg: "#0d2e26", border: "#16805f", num: D.green, sub: "#5c9d82" },
                  ];
                  return (
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 5 }}>
                      {cells.map((cell) => (
                        <div key={cell.label} className={cell.drill ? "duchet-drillable" : undefined} onClick={cell.drill}
                          style={{ background: cell.bg, border: `0.5px solid ${cell.border}`, borderRadius: 6, padding: exp ? 14 : 6 }}>
                          <div style={{ fontSize: exp ? 26 : 15, fontWeight: 500, color: cell.num }}>{cell.v}</div>
                          <div style={{ fontSize: exp ? 12 : 8, color: cell.sub }}>{cell.label}</div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </WidgetShell>

              <WidgetShell title="10. Качество заполнения" stripe={D.yellow}>
                {(exp) => wgLoading || !wg ? <Skel h={140} /> : !wg.w10_quality || !wg.w10_quality.doctors.length ? <NoData /> : (() => {
                  const q = wg.w10_quality;
                  const rows = exp ? q.doctors : q.doctors.slice(0, 5);
                  const maxV = Math.max(...q.doctors.map((r) => r.no_event_date), 1);
                  return (
                    <>
                      <div style={{ fontSize: exp ? 12 : 9, color: D.axis, marginBottom: 6 }}>
                        Визиты без даты записи. Такие записи не попадают в план, просрочку и неявку.
                      </div>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: exp ? 14 : 10 }}>
                        <tbody>
                          {rows.map((r) => (
                            <tr key={r.post_id} className="duchet-row-drill"
                              onClick={() => openCards("missing_event_date", r.post_id)}>
                              <td style={{ width: 14, padding: "3px 0" }}>
                                <span style={{ width: 8, height: 8, borderRadius: "50%", background: D.yellow,
                                  display: "inline-block" }} />
                              </td>
                              <td style={{ color: D.rowText, padding: "3px 4px", maxWidth: exp ? 320 : 90,
                                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                title={r.fio}>{r.fio}</td>
                              <td style={{ width: exp ? 220 : 70, padding: "3px 4px" }}>
                                <InlineBar frac={r.no_event_date / maxV} color={D.yellow} h={8} />
                              </td>
                              <td style={{ textAlign: "right", color: D.yellow, fontWeight: 500, padding: "3px 0",
                                whiteSpace: "nowrap" }}>
                                {fmt(r.no_event_date)}/{fmt(r.total)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {exp && !!q.doctors_total && q.doctors_total > rows.length && (
                        <div style={{ fontSize: 11, color: D.hint, marginTop: 6 }}>
                          показаны первые {rows.length} из {fmt(q.doctors_total)} врачей
                        </div>
                      )}
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: 5, marginTop: 8,
                        borderTop: `0.5px solid ${W.divider}`, paddingTop: 7 }}>
                        <MiniStat value={fmt(q.no_event_date)} label="без даты записи" color={D.yellow} />
                        <MiniStat value={fmtPct(q.pct)} label="от всех визитов" color={D.redText} />
                      </div>
                    </>
                  );
                })()}
              </WidgetShell>
            </div>
          </div>
        )}

        </div>{/* конец внутренней панели-«разворота» */}
      </div>

      {popOpen && (
        <PopulationModal misId={misId} onClose={(saved) => {
          setPopOpen(false);
          // сохранили — перечитываем дашборд, чтобы охват пересчитался от новой цифры
          if (saved) setReloadKey((k) => k + 1);
        }} />
      )}
    </div>
  );
}

// ── Ввод численности прикреплённого населения (только руководство) ──
// Реестр прикрепления от МИС приходит неполным, поэтому охват считался от обрезанного
// знаменателя. Введённая здесь цифра важнее реестра.
function PopulationModal({ misId, onClose }: { misId: string; onClose: (saved: boolean) => void }) {
  type AreaRow = { area: string; on_du: number; registry_population: number; population: number | null };
  const [rows, setRows] = useState<AreaRow[]>([]);
  const [vals, setVals] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");

  useEffect(() => {
    let alive = true;
    api<any>("/api/v1/duchet/dashboard/population", { mis: misId })
      .then((d) => {
        if (!alive) return;
        const list: AreaRow[] = d?.areas || [];
        setRows(list);
        const v: Record<string, string> = {};
        list.forEach((r) => { if (r.population != null) v[r.area] = String(r.population); });
        setVals(v);
      })
      .catch((e) => alive && setErr(String(e?.message || e)))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [misId]);

  const shown = rows.filter((r) => !q || r.area.toLowerCase().includes(q.toLowerCase()));
  const filled = Object.values(vals).filter((x) => x.trim() !== "").length;

  const save = async () => {
    const areas = Object.entries(vals)
      .map(([area, v]) => ({ area, population: Number(String(v).replace(/\s/g, "")) }))
      .filter((x) => Number.isFinite(x.population) && x.population >= 0 && String(vals[x.area]).trim() !== "");
    if (!areas.length) { setErr("Ничего не заполнено"); return; }
    setBusy(true); setErr("");
    try {
      await apiPut("/api/v1/duchet/dashboard/population", { mis_id: misId, areas });
      onClose(true);
    } catch (e: any) {
      setErr(String(e?.message || e).includes("403")
        ? "Недостаточно прав: вносить данные может только руководство"
        : String(e?.message || e));
      setBusy(false);
    }
  };

  const cell: React.CSSProperties = { padding: "4px 6px", borderTop: `1px solid ${W.divider}`, whiteSpace: "nowrap" };

  return (
    <>
      <div onClick={() => onClose(false)}
        style={{ position: "fixed", inset: 0, background: "rgba(4,10,24,0.7)", zIndex: 15000 }} />
      <div style={{ position: "fixed", zIndex: 15001, top: "50%", left: "50%", transform: "translate(-50%,-50%)",
        background: W.card, border: `1px solid ${W.cardBorder}`, borderRadius: 12, padding: 18,
        width: 760, maxWidth: "94vw", height: "80vh", color: SHELL.text, display: "flex", flexDirection: "column" }}>
        <div style={{ fontSize: 15, fontWeight: 500 }}>Прикреплённое население по участкам</div>
        <div style={{ fontSize: 12, color: D.axis, margin: "4px 0 10px", lineHeight: 1.45 }}>
          От этих цифр считается охват диспансерным наблюдением. Реестр прикрепления от МИС
          приходит неполным — на большинстве участков в нём людей меньше, чем стоит на учёте,
          поэтому охват там не показывается, пока не введена численность.
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Поиск участка"
          style={{ height: 32, borderRadius: 8, border: `1px solid ${W.cardBorder}`, background: SHELL.btnBg,
            color: "#fff", padding: "0 10px", fontSize: 13, marginBottom: 8, boxSizing: "border-box" }} />

        <div className="duchet-scroll" style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          {loading ? <Skel h={200} /> : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ color: D.axis, fontSize: 11, textAlign: "left" }}>
                  <th style={{ padding: "4px 6px" }}>Участок</th>
                  <th style={{ padding: "4px 6px", textAlign: "right" }}>На Д-учёте</th>
                  <th style={{ padding: "4px 6px", textAlign: "right" }}>В реестре МИС</th>
                  <th style={{ padding: "4px 6px", textAlign: "right", width: 130 }}>Прикреплено</th>
                </tr>
              </thead>
              <tbody>
                {shown.map((r) => {
                  const bad = r.registry_population > 0 && r.registry_population < r.on_du;
                  return (
                    <tr key={r.area}>
                      <td style={{ ...cell, color: D.rowText }}>{r.area}</td>
                      <td style={{ ...cell, textAlign: "right" }}>{fmt(r.on_du)}</td>
                      <td style={{ ...cell, textAlign: "right", color: bad ? D.redText : D.hint }}
                        title={bad ? "В реестре меньше людей, чем стоит на учёте — реестр неполный" : undefined}>
                        {fmt(r.registry_population)}
                      </td>
                      <td style={{ ...cell, textAlign: "right" }}>
                        <input value={vals[r.area] ?? ""} inputMode="numeric"
                          onChange={(e) => setVals({ ...vals, [r.area]: e.target.value })}
                          style={{ width: 110, height: 26, borderRadius: 6, border: `1px solid ${W.cardBorder}`,
                            background: SHELL.btnBg, color: "#fff", padding: "0 8px", fontSize: 13,
                            textAlign: "right", boxSizing: "border-box" }} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {err && <div style={{ color: D.redText, fontSize: 12, marginTop: 8 }}>{err}</div>}
        <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
          <div style={{ marginRight: "auto", fontSize: 11, color: D.hint }}>
            участков: {shown.length} · заполнено: {filled}
          </div>
          <button className="duchet-btn" onClick={() => onClose(false)}>Отмена</button>
          <button className="duchet-btn" disabled={busy} onClick={save}
            style={{ background: D.blue, borderColor: D.blue, opacity: busy ? 0.6 : 1 }}>
            {busy ? "Сохраняю…" : "Сохранить"}
          </button>
        </div>
      </div>
    </>
  );
}

function MiniStat({ value, label, color }: { value: string; label: string; color: string }) {
  return (
    <div style={{ background: "#13253d", borderRadius: 5, padding: 5, textAlign: "center" }}>
      <div style={{ fontSize: 14, fontWeight: 500, color }}>{value}</div>
      <div style={{ fontSize: 8, color: D.axis }}>{label}</div>
    </div>
  );
}


// ── Переход в МИС по строке таблицы Д-учёта ──
// МИС различает карту диспансерного наблюдения (docType=1) и диспансерный визит (2),
// поэтому одного UID мало — тип обязателен.
async function openDuchetMisDoc(docId: string, misId: string, docType: 1 | 2 = 1): Promise<void> {
  if (!docId || !misId) return;
  try {
    const res = await fetch(
      `${apiBase()}/api/v1/duchet/dashboard/mis-doc-url?docId=${encodeURIComponent(docId)}&mis=${encodeURIComponent(misId)}&docType=${docType}`,
      { headers: authHeaders(), credentials: "omit" },
    );
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      alert(d?.detail || `Не удалось открыть документ (HTTP ${res.status})`);
      return;
    }
    const data = await res.json();
    if (data?.url) window.open(data.url, "_blank", "noopener");
  } catch (e: any) {
    alert(`Не удалось открыть документ: ${e?.message || e}`);
  }
}

// ═══════════════════════ DRILL-ТАБЛИЦА (отдельное окно) ═══════════════════════
type CardRow = {
  iin: string; card_number: string; area: string; doctor: string; diagnosis: string; group: string;
  registered_date: string | null; age: number | null; sex: string;
  visits_planned: number; visits_attended: number; status: string;
};

const tdS: React.CSSProperties = { padding: "6px 10px", textAlign: "center", borderTop: `1px solid ${SHELL.border}`, whiteSpace: "nowrap" };

type VisitRow = {
  iin: string; doc_number: string; card_year: number | null; area: string | null; diagnosis: string | null;
  doctor: string; post_id: string; event_date: string | null; visit_date: string | null;
  visit_result: string | null; treatment_reason: string | null;
};

export function DuchetCardsPage(): JSX.Element {
  const qs = new URLSearchParams(window.location.search);
  const [visitRows, setVisitRows] = useState<VisitRow[]>([]);
  const [rows, setRows] = useState<CardRow[]>([]);
  const [total, setTotal] = useState(0);
  const [people, setPeople] = useState<number | null>(null);
  const [shown, setShown] = useState(0);
  const [trunc, setTrunc] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const drill = qs.get("drill") || "on_du";
  const bl = bucketLabel(drill, qs.get("bucket") || "");
  const title = (DRILL_TITLES[drill] || "Пациенты") + (bl ? ` · ${bl}` : "");

  const isVisits = drill === "missing_event_date" || drill === "doctor_visits";

  useEffect(() => {
    const p: Record<string, any> = {};
    qs.forEach((v, k) => { p[k] = v; });
    if (isVisits) {
      // визиты без даты записи: период не применяем — у таких записей даты нет
      const vp: Record<string, any> = { mis_id: p.mis_id };
      if (p.bucket) vp.doctor = p.bucket;
      // «визиты врача» — весь его приём, а не только записи без даты
      if (drill === "doctor_visits") vp.only_missing = false;
      api<{ items: VisitRow[]; total: number; shown?: number; truncated: boolean }>("/api/v1/duchet/dashboard/visits", vp)
        .then((d) => { setVisitRows(d.items); setTotal(d.total); setShown(d.shown ?? d.items.length); setPeople(null); setTrunc(d.truncated); })
        .catch((e) => setErr(String(e?.message || e))).finally(() => setLoading(false));
      return;
    }
    api<{ rows: CardRow[]; total: number; total_people?: number; shown?: number; truncated: boolean }>("/api/v1/duchet/dashboard/cards", p)
      .then((d) => { setRows(d.rows); setTotal(d.total); setPeople(d.total_people ?? null); setShown(d.shown ?? d.rows.length); setTrunc(d.truncated); })
      .catch((e) => setErr(String(e?.message || e))).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cols = isVisits
    ? ["№", "ИИН", "№ карты", "Год", "Участок", "Врач", "Диагноз", "Дата записи", "Дата посещения", "Результат"]
    : ["№", "ИИН", "№ карты", "Участок", "Врач", "Диагноз", "Группа", "Возр.", "Пол", "Дата взятия", "Назнач.", "Явился", "Статус"];

  // Поле строки под каждым столбцом; null — порядковый номер, по нему не сортируем.
  const sortKeys: (string | null)[] = isVisits
    ? [null, "iin", "doc_number", "card_year", "area", "doctor", "diagnosis", "event_date", "visit_date", "visit_result"]
    : [null, "iin", "card_number", "area", "doctor", "diagnosis", "group", "age", "sex", "registered_date", "visits_planned", "visits_attended", "status"];

  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<1 | -1>(1);

  const toggleSort = (key: string | null) => {
    if (!key) return;
    if (sortBy === key) setSortDir((d) => (d === 1 ? -1 : 1));
    else { setSortBy(key); setSortDir(1); }
  };

  function sortRows<T extends Record<string, any>>(list: T[]): T[] {
    if (!sortBy) return list;
    const out = [...list];
    out.sort((a, b) => {
      const x = a[sortBy], y = b[sortBy];
      // пустые всегда внизу, куда бы ни сортировали
      const xe = x === null || x === undefined || x === "";
      const ye = y === null || y === undefined || y === "";
      if (xe && ye) return 0;
      if (xe) return 1;
      if (ye) return -1;
      if (typeof x === "number" && typeof y === "number") return (x - y) * sortDir;
      return String(x).localeCompare(String(y), "ru") * sortDir;
    });
    return out;
  }

  const shownRows = sortRows(rows);
  const shownVisitRows = sortRows(visitRows);
  const csv = () => {
    const esc = (v: any) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [cols.map(esc).join(";")];
    rows.forEach((r, i) => lines.push([i + 1, r.iin, r.card_number, r.area, r.doctor, r.diagnosis, r.group, r.age, r.sex,
      r.registered_date ? r.registered_date.slice(0, 10).split("-").reverse().join(".") : "",
      r.visits_planned, r.visits_attended, r.status].map(esc).join(";")));
    const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "duchet_cards.csv"; a.click(); URL.revokeObjectURL(a.href);
  };

  return (
    <div style={{ minHeight: "100vh", background: SHELL.page, padding: 14, color: SHELL.text,
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <GlobalCss />
      <div style={{ maxWidth: 1400, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12, flexWrap: "wrap" }}>
          <b style={{ fontSize: 17, color: "#fff" }}>{title}</b>
          <span style={{ color: SHELL.textMuted, fontSize: 13 }}>
            {loading ? "загрузка…" : isVisits
              ? `визитов: ${fmt(total)}${trunc ? ` (показано ${fmt(shown)})` : ""}`
              : `пациентов: ${fmt(people ?? total)} · карт: ${fmt(total)}${trunc ? ` (показано ${fmt(shown)} строк)` : ""}`}
          </span>
          <button className="duchet-btn" onClick={csv} disabled={!rows.length}
            style={{ marginLeft: "auto", background: D.blue3, border: "none", fontWeight: 600 }}>
            Скачать CSV
          </button>
        </div>
        {err && <div style={{ color: D.redText, padding: 8 }}>{err}</div>}
        <div className="duchet-scroll-thin" style={{ overflowX: "auto", border: `1px solid ${SHELL.border}`, borderRadius: 10 }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, background: SHELL.card }}>
            <thead>
              <tr>{cols.map((c, i) => {
                const key = sortKeys[i];
                const active = key !== null && sortBy === key;
                return (
                  <th key={i} onClick={() => toggleSort(key)}
                    title={key ? "Сортировать по столбцу" : undefined}
                    style={{ background: "#0a3d66", padding: "8px 10px", position: "sticky", top: 0,
                      whiteSpace: "nowrap", textAlign: i <= 1 ? "left" : "center", color: "#fff",
                      cursor: key ? "pointer" : "default", userSelect: "none" }}>
                    {c}{active ? (sortDir === 1 ? " ▲" : " ▼") : ""}
                  </th>
                );
              })}</tr>
            </thead>
            <tbody>
              {isVisits && shownVisitRows.map((r, i) => (
                <tr key={r.iin + String(i)} className="duchet-row-drill"
                  title="Открыть документ в МИС"
                  onClick={() => openDuchetMisDoc(r.doc_number, qs.get("mis_id") || "", 2)}
                  style={{ background: i % 2 ? "rgba(75,159,232,0.06)" : "transparent" }}>
                  <td style={tdS}>{i + 1}</td>
                  <td style={{ ...tdS, textAlign: "left" }}>{r.iin}</td>
                  <td style={{ ...tdS, textAlign: "left" }}>{r.doc_number}</td>
                  <td style={tdS}>{r.card_year ?? ""}</td>
                  <td style={tdS}>{r.area ?? ""}</td>
                  <td style={{ ...tdS, textAlign: "left" }}>{r.doctor}</td>
                  <td style={tdS}>{r.diagnosis ?? ""}</td>
                  <td style={{ ...tdS, color: r.event_date ? SHELL.text : D.redText, fontWeight: r.event_date ? 400 : 700 }}>
                    {r.event_date ? r.event_date.slice(0, 10).split("-").reverse().join(".") : "не заполнена"}
                  </td>
                  <td style={tdS}>{r.visit_date ? r.visit_date.slice(0, 10).split("-").reverse().join(".") : ""}</td>
                  <td style={tdS}>{r.visit_result ?? ""}</td>
                </tr>
              ))}
              {!isVisits && shownRows.map((r, i) => (
                <tr key={r.iin + i} className="duchet-row-drill"
                  title="Открыть документ в МИС"
                  onClick={() => openDuchetMisDoc(r.card_number, qs.get("mis_id") || "", 1)}
                  style={{ background: i % 2 ? "rgba(75,159,232,0.06)" : "transparent" }}>
                  <td style={tdS}>{i + 1}</td>
                  <td style={{ ...tdS, textAlign: "left" }}>{r.iin}</td>
                  <td style={{ ...tdS, textAlign: "left" }}>{r.card_number}</td>
                  <td style={tdS}>{r.area}</td>
                  <td style={{ ...tdS, textAlign: "left" }}>{r.doctor}</td>
                  <td style={tdS}>{r.diagnosis}</td>
                  <td style={tdS}>{r.group}</td>
                  <td style={tdS}>{r.age ?? ""}</td>
                  <td style={tdS}>{r.sex}</td>
                  <td style={tdS}>{r.registered_date ? r.registered_date.slice(0, 10).split("-").reverse().join(".") : ""}</td>
                  <td style={tdS}>{r.visits_planned}</td>
                  <td style={tdS}>{r.visits_attended}</td>
                  <td style={{ ...tdS, color: r.status === "снят" ? SHELL.textMuted : D.greenText, fontWeight: 700 }}>{r.status}</td>
                </tr>
              ))}
              {!loading && !(isVisits ? visitRows.length : rows.length) && (
                <tr><td colSpan={cols.length} style={{ padding: 16, textAlign: "center", color: SHELL.textMuted }}>Нет данных</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
